import { useRef } from 'react';
import {
  ActionIcon,
  Avatar,
  Box,
  Group,
  Menu,
  Stack,
  Text,
} from '@mantine/core';
import { BsThreeDots } from 'react-icons/bs';
import { IoMailOpenOutline, IoMailUnreadOutline } from 'react-icons/io5';
import useAvatarImageFallback from '@hooks/useAvatarImageFallback';

interface InboxMessageListItemProps {
  avatarUrl?: string;
  name: string;
  subtitle?: string;
  timestamp: string;
  hasUnread: boolean;
  onClick: () => void;
  onToggleRead?: () => void;
}

export default function InboxMessageListItem({
  avatarUrl,
  name,
  subtitle,
  timestamp,
  hasUnread,
  onClick,
  onToggleRead,
}: InboxMessageListItemProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  useAvatarImageFallback(imageRef);

  return (
    <Box onClick={onClick} py="sm" style={{ cursor: 'pointer' }}>
      <Group gap="sm" align="flex-start" wrap="nowrap">
        <Avatar ref={imageRef} src={avatarUrl} alt={name} size={40} />

        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
              {hasUnread && (
                <Box
                  w={8}
                  h={8}
                  bg="var(--yellow)"
                  style={{ borderRadius: '50%', flexShrink: 0 }}
                />
              )}
              <Text fw={hasUnread ? 700 : 400} size="sm" truncate>
                {name}
              </Text>
            </Group>
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {timestamp}
            </Text>
          </Group>

          {subtitle && (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {subtitle}
            </Text>
          )}
        </Stack>

        {onToggleRead && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={(e) => e.stopPropagation()}
              >
                <BsThreeDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={
                  hasUnread ? (
                    <IoMailOpenOutline size={14} />
                  ) : (
                    <IoMailUnreadOutline size={14} />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleRead();
                }}
              >
                Mark as {hasUnread ? 'read' : 'unread'}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
    </Box>
  );
}
